// Строители schema.org (JSON-LD). Чистые функции без DOM, используются
// и пререндером (уходят в HTML), и RouteSeo (обновляют <head> при SPA-переходе).

import { FAQ as HOME_FAQ } from '@/data/faq';
import { SUPPORT_FAQ } from '@/data/supportFaq';
import {
  ANDROID_MIN,
  APK_URL,
  APK_VERSION,
  APP_ICON,
  IOS_MIN,
  IOS_URL,
  WIN_MIN,
  WIN_URL,
  WIN_VERSION,
} from '@/data/downloads';
import {
  CONTACTS,
  LEGAL_ENTITY,
  PARTNER_PRICE_RUB,
  PRICE_FROM_RUB,
  SITE_NAME,
  SITE_URL,
} from './site';

export type JsonLd = Record<string, unknown>;

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;
const LOGO_URL = `${SITE_URL}${APP_ICON}`;

/** Тексты в data/* набраны с неразрывными пробелами, в разметке нужны обычные. */
function plain(text: string): string {
  return text.replace(/\u00a0|&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

export function organization(): JsonLd {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    alternateName: 'Resstro SaaS',
    legalName: LEGAL_ENTITY.name,
    taxID: LEGAL_ENTITY.inn,
    url: `${SITE_URL}/`,
    logo: { '@type': 'ImageObject', url: LOGO_URL, width: 512, height: 512 },
    description:
      'Облачная система управления рестораном: касса, админ-панель и электронное QR-меню в одной подписке.',
    founder: { '@type': 'Person', name: LEGAL_ENTITY.founder },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'RU',
      addressRegion: LEGAL_ENTITY.region,
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: CONTACTS.phone,
        contactType: 'customer support',
        email: CONTACTS.supportEmail,
        availableLanguage: ['Russian'],
        areaServed: 'RU',
      },
    ],
    sameAs: [CONTACTS.whatsapp, CONTACTS.telegram],
  };
}

export function webSite(): JsonLd {
  return {
    '@type': 'WebSite',
    '@id': SITE_ID,
    url: `${SITE_URL}/`,
    name: SITE_NAME,
    inLanguage: 'ru-RU',
    publisher: { '@id': ORG_ID },
  };
}

/** Основной продукт, подписка Resstro (главная). */
export function resstroProduct(): JsonLd {
  return {
    '@type': 'SoftwareApplication',
    name: 'Resstro',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Restaurant Management',
    operatingSystem: 'Web, iPadOS, Android, Windows',
    url: `${SITE_URL}/`,
    image: LOGO_URL,
    description:
      'Касса, админ-панель и электронное QR-меню для ресторанов и кафе. Серверы в России, без процента с оборота.',
    featureList: [
      'Касса для официантов и кассиров (iPad, Android, Windows)',
      'Админ-панель: меню, сотрудники, смены, аналитика',
      'Электронное меню по QR с онлайн-оплатой',
      'Закуп у поставщиков прямо из кассы',
    ],
    offers: {
      '@type': 'Offer',
      price: String(PRICE_FROM_RUB),
      priceCurrency: 'RUB',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/#pricing`,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: String(PRICE_FROM_RUB),
        priceCurrency: 'RUB',
        unitText: 'MONTH',
        referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'заведение' },
      },
    },
    publisher: { '@id': ORG_ID },
  };
}

/** Кабинет партнёра (поставщик / оптовик), отдельная подписка 2 999 ₽/мес. */
export function partnerProduct(name: string, path: string, description: string): JsonLd {
  return {
    '@type': 'SoftwareApplication',
    name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${SITE_URL}${path}`,
    description,
    offers: {
      '@type': 'Offer',
      price: String(PARTNER_PRICE_RUB),
      priceCurrency: 'RUB',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}${path}`,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: String(PARTNER_PRICE_RUB),
        priceCurrency: 'RUB',
        unitText: 'MONTH',
      },
    },
    publisher: { '@id': ORG_ID },
  };
}

/** Мобильное/десктопное приложение кассы (страница /downloads). Приложение бесплатное, платная подписка. */
export function cashierApp(): JsonLd {
  return {
    '@type': 'SoftwareApplication',
    name: 'Resstro Касса',
    applicationCategory: 'BusinessApplication',
    operatingSystem: `${WIN_MIN}, ${ANDROID_MIN}, ${IOS_MIN}`,
    softwareVersion: APK_VERSION,
    url: `${SITE_URL}/downloads`,
    image: LOGO_URL,
    description:
      'Рабочее место официанта и кассира: приём заказов за столами, оплаты, печать чеков, синхронизация с админ-панелью в реальном времени.',
    downloadUrl: [`${SITE_URL}${APK_URL}`, `${SITE_URL}${WIN_URL}`],
    installUrl: IOS_URL,
    releaseNotes: `Android ${APK_VERSION}, Windows ${WIN_VERSION}`,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'RUB' },
    publisher: { '@id': ORG_ID },
  };
}

/** Статья документации (/docs/*). */
export function techArticle(headline: string, path: string, description: string): JsonLd {
  return {
    '@type': 'TechArticle',
    headline,
    description,
    url: `${SITE_URL}${path}`,
    inLanguage: 'ru-RU',
    publisher: { '@id': ORG_ID },
  };
}

export function faqPage(items: Array<{ question: string; answer: string }>): JsonLd {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: plain(item.question),
      acceptedAnswer: { '@type': 'Answer', text: plain(item.answer) },
    })),
  };
}

export function homeFaq(): JsonLd {
  return faqPage(HOME_FAQ);
}

export function supportFaq(): JsonLd {
  return faqPage(SUPPORT_FAQ.map((i) => ({ question: i.q, answer: i.a })));
}

export function breadcrumbs(trail: Array<{ name: string; path: string }>): JsonLd {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path === '/' ? '/' : crumb.path}`,
    })),
  };
}

/** Собирает несколько сущностей в один документ через @graph. */
export function graph(...nodes: JsonLd[]): JsonLd {
  return { '@context': 'https://schema.org', '@graph': nodes };
}
